import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react'
import { waApi } from '../../api/whatsapp'

/** One navigable attachment. `messageId` is the wa_messages row id the blob is fetched by. */
export interface WaViewerItem {
  messageId: string
  mime: string | null
  name: string | null
}

/**
 * In-CRM viewer for WhatsApp attachments, navigable across every attachment in
 * the list it was opened from (arrow keys or the header chevrons).
 *
 * The blob is fetched through react-query on the same `['wa-media', …]` key the
 * chat bubbles use, so stepping back to an already-seen image is instant and —
 * more importantly — `mediaBlob()` mints a fresh object URL per call, so sharing
 * the cache is what stops repeated navigation leaking them.
 */
export function MediaViewerModal({ companyId, items, index, onIndexChange, onClose }: {
  companyId: string
  items: WaViewerItem[]
  index: number
  onIndexChange: (next: number) => void
  onClose: () => void
}) {
  const item = items[index]
  const hasPrev = index > 0
  const hasNext = index < items.length - 1

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1)
      if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, onIndexChange, index, hasPrev, hasNext])

  const { data: url, isLoading } = useQuery({
    queryKey: ['wa-media', companyId, item?.messageId],
    enabled: !!item,
    staleTime: Infinity,
    queryFn: () => waApi.mediaBlob(companyId, item.messageId),
  })

  if (!item) return null
  const type = item.mime ?? ''

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-black/80" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-2 text-white">
        <span className="truncate text-sm">
          {item.name ?? 'Attachment'}
          {items.length > 1 && (
            <span className="ml-2 text-xs text-gray-400">{index + 1} / {items.length}</span>
          )}
        </span>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          {items.length > 1 && (
            <>
              <button
                className="btn btn-ghost btn-sm text-white disabled:opacity-30"
                title="Previous (←)"
                disabled={!hasPrev}
                onClick={() => onIndexChange(index - 1)}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                className="btn btn-ghost btn-sm text-white disabled:opacity-30"
                title="Next (→)"
                disabled={!hasNext}
                onClick={() => onIndexChange(index + 1)}
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
          {url && (
            <a href={url} download={item.name ?? 'file'} className="btn btn-ghost btn-sm text-white">
              <Download size={16} /> Download
            </a>
          )}
          <button className="btn btn-ghost btn-sm text-white" onClick={onClose}><X size={18} /></button>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center overflow-auto p-4" onClick={e => e.stopPropagation()}>
        {isLoading && <div className="text-sm text-gray-300">Loading…</div>}
        {!isLoading && url && (
          <>
            {type.startsWith('image/') && (
              // key: force a fresh element per attachment so the browser never
              // shows the previous image while the next one decodes.
              <img key={item.messageId} src={url} className="max-h-full max-w-full object-contain" alt={item.name ?? ''} />
            )}
            {type.startsWith('video/') && (
              <video key={item.messageId} src={url} controls autoPlay className="max-h-full max-w-full" />
            )}
            {type.startsWith('audio/') && (
              <audio key={item.messageId} src={url} controls autoPlay className="w-full max-w-md" />
            )}
            {type === 'application/pdf' && (
              <iframe key={item.messageId} src={url} title={item.name ?? 'PDF'} className="h-full w-full bg-white" />
            )}
            {!type.startsWith('image/') && !type.startsWith('video/') && !type.startsWith('audio/') && type !== 'application/pdf' && (
              <div className="rounded-lg bg-white p-8 text-center">
                <p className="mb-3 text-gray-600">This file type can't be previewed.</p>
                <a href={url} download={item.name ?? 'file'} className="btn btn-primary">
                  <Download size={16} /> Download {item.name ?? 'file'}
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
